import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Page, TableScroller, Tabs } from '../../components/common';
import { formatBytes, formatRelative } from '../../lib/format';
import { fmtDay, fmtInt, useParam } from '../../pages/statistics/shared';
import { BarChart } from '@nyce/admin-kit/console';
import { IssueList, Kpi } from '../../pages/dashboard/shared';
import { usePrintQueueLinks } from '../links';
import { TenantCell, isStuck, pqPath } from '../shared';
import {
  RECENT,
  USAGE_RANGES,
  countBy,
  jobsPerDay,
  printQueueIssues,
  printedPerDay,
  tenantRows,
  usageSince,
  usageWindow,
  usePrintQueueSummary,
  type PrintQueueSummary,
  type UsageRange,
} from './summary';
import '../../pages/statistics/statistics.css';
import '../printqueue.css';

const TOP = 15;

/** Printed jobs per day from the usage counters, with today / this month / the range in figures and bytes. */
function UsageCharts({ s, days }: { s: PrintQueueSummary; days: UsageRange }) {
  const u = s.usage!;
  const w = usageWindow(u, days);
  const today = usageSince(u, w.today);
  const month = usageSince(u, w.monthStart);
  const range = usageSince(u, w.from);
  return (
    <div className="bd-grid">
      <BarChart
        title={`Jobs printed per day (${days} days)`}
        bars={printedPerDay(u, days).map((b) => ({ ...b, label: fmtDay(b.key) }))}
        format={(v) => fmtInt(v)}
        empty={`No jobs printed in the last ${days} days.`}
      />
      <div className="nyk-card">
        <div className="nyk-card-head">
          <h2>Printed</h2>
        </div>
        <ul className="nyk-group-card-lines" data-testid="pq-usage">
          <li>
            Today <b>{fmtInt(today.printed)}</b> printed · <b>{fmtInt(today.failed)}</b> failed ·{' '}
            <b>{fmtInt(today.added)}</b> added
          </li>
          <li>
            This month <b>{fmtInt(month.printed)}</b> printed · {formatBytes(month.printedBytes)}
          </li>
          <li>
            Last {days} days <b>{fmtInt(range.printed)}</b> printed · {formatBytes(range.printedBytes)} ·{' '}
            <b>{fmtInt(range.failed)}</b> failed
          </li>
          <li className="nyk-ui-hint">Days in {u.timeZone}</li>
        </ul>
      </div>
    </div>
  );
}

function EnvironmentSection({ s, days }: { s: PrintQueueSummary; days: UsageRange }) {
  const target = s.target.id;
  const links = usePrintQueueLinks(target);
  const link = (path: string) => pqPath(path, target);
  const open = countBy(s.open);
  const stuck = s.open.filter((j) => isStuck(j)).length;
  const servers = new Set([
    ...(s.printers ?? []).map((p) => `${p.tenantId}\u0000${p.printServerName}`),
    ...(s.printServers ?? []).map((p) => `${p.tenantId}\u0000${p.name}`),
  ]);
  const live = s.printServers?.filter((p) => p.state !== 'offline').length;
  const rows = tenantRows(s);
  const recent = countBy(s.recent);
  return (
    <section className="oph-dash-section" aria-label={`Print queue ${s.target.label}`} data-target={target}>
      <div className="oph-dash-section-head">
        <h2>{s.target.label}</h2>
      </div>
      {s.error ? (
        <ErrorBox error={s.error} />
      ) : (
        <>
          {s.partErrors.length > 0 && <ErrorBox error={s.partErrors.join(' · ')} />}
          <div className="nyk-kpis">
            <Kpi
              label="Waiting"
              value={open.Registered}
              sub="not fetched by a print server"
              to={link('/printqueue/jobs?status=Registered')}
            />
            <Kpi
              label="At print servers"
              value={open.Fetched + open.Printing}
              sub={`${open.Fetched} fetched · ${open.Printing} printing`}
              to={link('/printqueue/jobs?status=open')}
            />
            <Kpi
              label="Stuck"
              value={stuck}
              sub="unfinished for > 15 min"
              tone={stuck ? 'red' : undefined}
              to={link('/printqueue/jobs?status=stuck')}
            />
            <Kpi
              label="Failed"
              value={open.Error}
              tone={open.Error ? 'amber' : undefined}
              to={link('/printqueue/jobs?status=Error')}
            />
            <Kpi
              label="Print servers"
              value={s.printers ? servers.size : '—'}
              sub={`${live === undefined ? '' : `${live} online · `}${s.printers?.length ?? 0} printers`}
              to={link('/printqueue/printservers')}
            />
            <Kpi label="Tokens" value={s.tokens?.length ?? '—'} to={link('/printqueue/tokens')} />
          </div>
          {s.openTruncated && (
            <p className="nyk-ui-hint">More than 5000 unfinished jobs: the counts are a lower bound.</p>
          )}
          {s.usage ? (
            <UsageCharts s={s} days={days} />
          ) : (
            <div className="bd-grid">
              <BarChart
                title={`Jobs per day (newest ${RECENT})`}
                bars={jobsPerDay(s.recent)}
                format={(v) => String(v)}
                empty="No jobs in the last 14 days."
              />
              <div className="nyk-card">
                <div className="nyk-card-head">
                  <h2>Newest {s.recent.length} jobs</h2>
                </div>
                <ul className="nyk-group-card-lines" data-testid="pq-recent-statuses">
                  <li>
                    <b>{recent.Printed}</b> printed · <b>{recent.Error}</b> failed ·{' '}
                    <b>{recent.Registered + recent.Fetched + recent.Printing}</b> unfinished
                  </li>
                  {s.recent[0]?.dateAdded && <li>Newest {formatRelative(s.recent[0].dateAdded)}</li>}
                  {links.reason && <li className="nyk-ui-hint">Tenants unlinked: {links.reason}</li>}
                </ul>
              </div>
            </div>
          )}
          <div className="nyk-card">
            <div className="nyk-card-head">
              <h2>Tenants</h2>
              <span className="nyk-ui-hint">{rows.length}</span>
            </div>
            <TableScroller>
              <table className="nyk-ui-table nyk-cards" data-testid="pq-tenants">
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Print servers</th>
                    <th className="nyk-ui-num">Printers</th>
                    <th className="nyk-ui-num">Unfinished</th>
                    <th className="nyk-ui-num">Failed</th>
                    {s.usage && <th className="nyk-ui-num">Printed this month</th>}
                    <th>Last job</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, TOP).map((r) => (
                    <tr key={r.tenantId} data-tenant={r.tenantId}>
                      <td className="nyk-card-title">
                        <TenantCell tenantId={r.tenantId} links={links} target={target} />
                      </td>
                      <td data-label="Print servers">{r.servers.join(', ') || '—'}</td>
                      <td className="nyk-ui-num" data-label="Printers">
                        {r.printers}
                      </td>
                      <td className="nyk-ui-num" data-label="Unfinished">
                        <Link
                          to={link(`/printqueue/jobs?tenant=${encodeURIComponent(r.tenantId)}&status=open`)}
                        >
                          {r.open}
                        </Link>
                        {r.stuck > 0 && (
                          <Link
                            className="nyk-text-red"
                            to={link(
                              `/printqueue/jobs?tenant=${encodeURIComponent(r.tenantId)}&status=stuck`
                            )}
                          >
                            {' '}
                            ({r.stuck} stuck)
                          </Link>
                        )}
                      </td>
                      <td className="nyk-ui-num" data-label="Failed">
                        <Link
                          to={link(`/printqueue/jobs?tenant=${encodeURIComponent(r.tenantId)}&status=Error`)}
                        >
                          {r.errors}
                        </Link>
                      </td>
                      {s.usage && (
                        <td className="nyk-ui-num" data-label="Printed this month">
                          {fmtInt(r.printedMonth)}
                          {!!r.printedMonthBytes && (
                            <span className="nyk-ui-hint"> · {formatBytes(r.printedMonthBytes)}</span>
                          )}
                        </td>
                      )}
                      <td data-label="Last job">{r.lastJob ? formatRelative(r.lastJob) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroller>
            {!rows.length && <EmptyState title="No tenants with printers or jobs." />}
            {rows.length > TOP && (
              <p className="nyk-ui-hint">and {rows.length - TOP} more without problems</p>
            )}
          </div>
        </>
      )}
    </section>
  );
}

/** Print queue dashboard: per environment the jobs by state, stuck and failed ones, printers, and tenants. */
export function PrintQueueDashboardPage() {
  const res = usePrintQueueSummary();
  const list = res.data ?? [];
  const [daysParam, setDays] = useParam('days', '14');
  const days = (USAGE_RANGES.find((d) => String(d) === daysParam) ?? 14) as UsageRange;
  return (
    <Page
      title="Print queue"
      description="Print jobs per environment: waiting, at the print servers, stuck and failed, with printers and tenants. Cached for a minute."
      actions={
        <>
          {list.some((s) => s.usage) && (
            <Tabs
              aria-label="Chart range"
              value={String(days)}
              onChange={setDays}
              options={USAGE_RANGES.map((d) => ({ value: String(d), label: `${d} d` }))}
            />
          )}
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={res.loading}
            onClick={res.reload}
          >
            Refresh
          </Button>
        </>
      }
    >
      <ErrorBox error={res.error} />
      {!res.enabled && <EmptyState card title="No print queue access" hint="Needs ophub:printqueue:read." />}
      {res.enabled && !res.data && res.loading && <div className="oph-dash-skeleton" aria-busy="true" />}
      {res.data && (
        <div className={res.loading ? 'oph-dash-stale' : undefined}>
          <IssueList
            issues={printQueueIssues(list)}
            title="Print queue issues"
            clear="No print queue issues."
            testId="pq-issues"
          />
          {list.map((s) => (
            <EnvironmentSection key={s.target.id} s={s} days={days} />
          ))}
          {!list.length && (
            <EmptyState
              card
              title="No print queue configured"
              hint="Set PrintQueueUrl on an AuthTargets entry and the hub’s PrintQueue:Tokens:<id>."
            />
          )}
        </div>
      )}
    </Page>
  );
}
